import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Z_INDEX_LAYER, MODAL_SIZE } from '@CommonConstants';

import { ReactComponent as DeleteSVG } from '@Assets/Svgs/ic_delete.svg';

const getVisibleModalCSS = () => `
  top: -${window.scrollY}px;
  left: 0;
  right: 0;
  position: fixed;
  overflow-y: hidden;
  pointerEvents: 'none';
  width: 100%;
`;

const WINDOW_SIZE = {
  WIDTH: window.innerWidth,
  HEIGHT: window.innerHeight,
};

const Modal = ({ visible, isBackDropClickable, isShowCloseButton, size, handleCloseButtonClick, style, children }) => {
  const body = document.querySelector('body');

  useEffect(() => {
    if (visible) {
      body.style.cssText = getVisibleModalCSS();
    }

    return () => {
      if (visible) {
        const scrollY = body.style.top;

        body.style.cssText = '';
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    };
  }, [visible]);

  const onBackDropClick = event => {
    const { target, currentTarget } = event;

    if (target === currentTarget) {
      handleCloseButtonClick(event);
    }
  };

  const onCloseButtonClick = () => handleCloseButtonClick();

  if (!visible) {
    return null;
  }

  return (
    <ModalContainer>
      <ModalOverlay />
      <ModalWrapper tabIndex={-1} onClick={isBackDropClickable ? onBackDropClick : null}>
        <ModalInner tabIndex={0} size={size} style={style}>
          {isShowCloseButton && (
            <ModalHeader>
              <CloseIconWrapper onClick={onCloseButtonClick}>
                <CloseIcon />
              </CloseIconWrapper>
            </ModalHeader>
          )}

          <ModalContent isShowCloseButton={isShowCloseButton}>{children}</ModalContent>
        </ModalInner>
      </ModalWrapper>
    </ModalContainer>
  );
};

Modal.propTypes = {
  visible: PropTypes.bool,
  isBackDropClickable: PropTypes.bool,
  isShowCloseButton: PropTypes.bool,
  size: PropTypes.oneOf([MODAL_SIZE.BIG, MODAL_SIZE.NORMAL]),
  style: PropTypes.shape({}),
  handleCloseButtonClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

Modal.defaultProps = {
  visible: false,
  isBackDropClickable: true,
  isShowCloseButton: true,
  size: MODAL_SIZE.NORMAL,
  style: {},
  handleCloseButtonClick: () => {},
};

const ModalCSS = css`
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const FlexAlignCenterDiv = styled.div`
  display: flex;
  align-items: center;
`;

const ModalContainer = styled.div``;

const ModalOverlay = styled.div`
  ${ModalCSS}

  background-color: rgba(0, 0, 0, 0.2);
  z-index: ${Z_INDEX_LAYER.LEVEL_10};
`;

const ModalWrapper = styled.div`
  ${ModalCSS}
  display: flex;
  justify-content: center;
  align-items: center;

  z-index: ${Z_INDEX_LAYER.LEVEL_10};
  outline: 0;
`;

const ModalInner = styled.div`
  position: relative;
  box-sizing: border-box;
  margin: 0 auto;

  border-radius: 16px;
  z-index: ${Z_INDEX_LAYER.LEVEL_10};

  width: min(${WINDOW_SIZE.WIDTH - 40}px, 752px);
  height: min(${WINDOW_SIZE.HEIGHT - 60}px, 600px);

  ${({ size, theme }) => css`
    background-color: ${theme.colors.white};
    width: ${size === MODAL_SIZE.NORMAL && '360px'};
    height: ${size === MODAL_SIZE.NORMAL && 'auto'};
  `};
`;

const ModalHeader = styled(FlexAlignCenterDiv)`
  width: 100%;
  height: 48px;
  padding: 10px;

  justify-content: flex-end;
`;

const ModalContent = styled(FlexAlignCenterDiv)`
  flex-direction: column;

  padding: ${({ isShowCloseButton }) => (isShowCloseButton ? '16px 20px 32px 20px' : '20px 20px 32px 20px')};
`;

const CloseIconWrapper = styled(FlexAlignCenterDiv)`
  width: 32px;
  height: 32px;
  justify-content: center;

  cursor: pointer;
`;

const CloseIcon = styled(DeleteSVG)`
  width: 16px;
  height: 16px;

  stroke: ${({ theme }) => theme.colors.ui_on_3};
`;

export default Modal;
